import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Params, useParams } from 'react-router-dom';

import {
  type CheckDepositBatch as ApiCheckDepositBatch,
  type ListTransfers as ApiListTransfers,
  type UnifiedTransfer,
  type UpdateCheckDepositBatchRequest,
  transferCheckDepositbatchGetBatch,
  transferCheckDepositbatchGetBatchQueryKey,
  transferCheckDepositbatchUpdateBatch,
  transferSearchTransfers,
  transferSearchTransfersQueryKey,
} from '@column/api';
import { Dropdown, Icon, Input } from '@column/column-ui-kit';
import { Badge, Button, DataTable, Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@column/ui';

import { PageActions } from '~/app/Layout';
import { BankAccountCard, Loading, Pagination, RenderFields, SectionHeader } from '~/components';
import { LogoLoading } from '~/elements';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { transferTableColumns } from '~/lib/transfers';
import { getTransferURL } from '~/lib/transfers/utils';
import { TransferEntryType } from '~/repositories';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { PAGE_SIZE_OPTIONS, useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { Inner } from '~/styles';
import { formatDateTime } from '~/util';

import { TransferBatchHeader } from './_components/TransferBatchHeader';

export const PageTransferBatchesView: React.FC = () => {
  const { transferBatchId } = useParams<keyof Params>() as Params;
  const navigate = useNavigate();
  const { handleFormErrors } = useFormErrorHandler();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { pageSize, timeFormat, update: updateTableConfig } = useTableConfig('transfers');
  const { cursor, hasPrevious, onNext, onPrevious, pageIndex, setPageSize, ...pagination } = usePaginationState({
    defaultPageSize: pageSize,
  });
  const [entriesOpen, setEntriesOpen] = React.useState(false);

  const queryClient = useQueryClient();
  const { data: transferBatch, isLoading: isTransferBatchLoading } = useQuery<ApiCheckDepositBatch>({
    queryKey: transferCheckDepositbatchGetBatchQueryKey({
      path: { check_deposit_batch_id: transferBatchId ?? '' },
    }),
    queryFn: async () => {
      const { data } = await transferCheckDepositbatchGetBatch({
        path: { check_deposit_batch_id: transferBatchId ?? '' },
        throwOnError: true,
      });
      return data as ApiCheckDepositBatch;
    },
    enabled: !!transferBatchId,
    gcTime: 0,
    staleTime: 0,
  });

  const { mutate: updateTransferBatch, isPending: isUpdateTransferBatchPending } = useMutation<
    ApiCheckDepositBatch,
    Error,
    UpdateCheckDepositBatchRequest
  >({
    mutationFn: async (request) => {
      const { data } = await transferCheckDepositbatchUpdateBatch({
        path: { check_deposit_batch_id: transferBatchId ?? '' },
        body: request,
        throwOnError: true,
      });
      return data as ApiCheckDepositBatch;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        transferCheckDepositbatchGetBatchQueryKey({
          path: { check_deposit_batch_id: transferBatchId ?? '' },
        }),
        data
      );
      addSuccessNotification({
        content: 'Transfer batch updated',
      });
    },
    onError: (error) => {
      addDangerNotification({
        content: error instanceof Error ? error.message : 'An unknown error occurred',
      });
    },
  });

  const { data: transfersData, isLoading: isTransfersLoading } = useQuery<ApiListTransfers>({
    queryKey: transferSearchTransfersQueryKey({
      query: {
        limit: pagination.pageSize,
        starting_after: cursor,
        transfer_batch_id: transferBatchId ?? '',
      },
    }),
    queryFn: async () => {
      const { data } = await transferSearchTransfers({
        query: {
          limit: pagination.pageSize,
          starting_after: cursor,
          transfer_batch_id: transferBatchId ?? '',
        },
        throwOnError: true,
      });
      return data as ApiListTransfers;
    },
    enabled: !!transferBatchId,
    gcTime: 0,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });

  const formMethods = useForm<UpdateCheckDepositBatchRequest>({
    defaultValues: {
      description: transferBatch?.description ?? '',
    },
  });
  // reset() (not setValue) so the loaded value becomes the dirty baseline: the Save button stays disabled
  // until the user actually edits, and re-disables once a save round-trips the new value back.
  useEffect(() => {
    formMethods.reset({ description: transferBatch?.description ?? '' });
  }, [transferBatch?.description, transferBatch?.id]);

  const handleTimeFormatChange = useCallback(
    (value: TableTimeFormat) => {
      updateTableConfig({ timeFormat: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save time format preference' });
      });
    },
    [addDangerNotification, updateTableConfig]
  );

  const columns = useMemo(
    () =>
      transferTableColumns({
        timeFormat,
        onTimeFormatChange: handleTimeFormatChange,
      }),
    [handleTimeFormatChange, timeFormat]
  );

  const transfers = useMemo(() => transfersData?.transfers ?? [], [transfersData]);
  const tableMinHeight = transfers.length === 0 ? 341 : undefined;
  const isTableInitialLoading = isTransfersLoading && transfers.length === 0;

  const handleNext = () => {
    if (!transfersData || transfersData.transfers.length === 0) return;
    onNext({ cursor: transfersData.transfers[transfersData.transfers.length - 1].id });
  };

  // Read isDirty during render so react-hook-form subscribes and re-renders (and both Save buttons
  // re-enable) the moment an editable field changes.
  const { isDirty } = formMethods.formState;
  const isSaveDisabled = isUpdateTransferBatchPending || !isDirty;
  const handleSave = formMethods.handleSubmit(
    (data) => updateTransferBatch(data),
    (errors) => handleFormErrors({ errors })
  );
  const saveButton = (
    <Button size="sm" disabled={isSaveDisabled} onClick={handleSave}>
      {isUpdateTransferBatchPending ? 'Saving...' : 'Save Changes'}
    </Button>
  );

  return (
    <>
      {/* Mirror the bottom Save button up in the page header's top-right actions slot. */}
      <PageActions>{saveButton}</PageActions>
      <Loading isLoading={isTransferBatchLoading} fullHeight>
        {transferBatch && (
          <TransferBatchHeader
            amount={transferBatch.total_amount}
            createdAt={transferBatch.created_at}
            currencyCode={transferBatch.currency_code}
            transferBatchId={transferBatch.id}
            totalCount={transferBatch.total_count}
            status={transferBatch.status}
          />
        )}
        <SectionHeader text="Batch Information" border />
        <Inner pt={0} px="var(--page-margin)">
          <form>
            <FormProvider {...formMethods}>
              <RenderFields
                sections={[
                  {
                    variant: 'isCompact',
                    fields: [
                      {
                        id: 'description',
                        label: 'Description',
                        children: ({ onBlur, onChange, value }, { isTouched, error }) => (
                          <Input
                            onChange={onChange}
                            onBlur={onBlur}
                            value={String(value)}
                            hasError={isTouched && !!error}
                          />
                        ),
                      },
                      {
                        id: 'bankAccount',
                        label: 'Bank Account',
                        children: () =>
                          transferBatch?.bank_account_id ? (
                            <BankAccountCard variant="detailed" bankAccountId={transferBatch.bank_account_id} />
                          ) : null,
                      },
                      {
                        id: 'closedAt',
                        label: 'Closes At',
                        children: () =>
                          transferBatch?.closed_at
                            ? formatDateTime(transferBatch.closed_at, { timeZoneFormat: 'local' })
                            : '—',
                      },
                    ],
                  },
                ]}
              />
            </FormProvider>
          </form>
        </Inner>
        <SectionHeader text="Transfers" border />
        <Loading isLoading={isTableInitialLoading}>
          <Inner pt={0} px="var(--page-margin)">
            <DataTable<UnifiedTransfer>
              columns={columns}
              data={transfers}
              isLoading={false}
              loadingContent={<LogoLoading />}
              style={{ minHeight: tableMinHeight }}
              emptyContent={
                <Empty className="min-h-[300px]">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Icon.AnimationArrows />
                    </EmptyMedia>
                    <EmptyTitle>No transfers found</EmptyTitle>
                    <EmptyDescription>This batch does not have any transfers yet.</EmptyDescription>
                  </EmptyHeader>
                </Empty>
              }
              rowClassName="min-h-11"
              rowKey={(row) => row.id}
              onRowClick={(row) => navigate(getTransferURL(row.id, row.type as TransferEntryType))}
            />

            {(hasPrevious || transfersData?.has_more) && (
              <div className="relative z-[1] flex items-center justify-between pt-4">
                <Dropdown
                  options={PAGE_SIZE_OPTIONS.map((entries: number) => ({
                    label: entries.toString(),
                    value: entries,
                  }))}
                  size="small"
                  variant="muted"
                  active={pagination.pageSize}
                  customLabel={
                    <Badge
                      variant="secondary"
                      className="rounded-[8px] [&>svg]:ml-[-4px] [&>svg]:inline-block [&>svg]:align-top"
                    >
                      {pagination.pageSize}
                      {entriesOpen ? <Icon.ChevronUp /> : <Icon.ChevronDown />}
                    </Badge>
                  }
                  onOpenChange={setEntriesOpen}
                  onChange={setPageSize}
                />
                <Pagination
                  current={pageIndex + 1}
                  disablePrev={!hasPrevious}
                  disableNext={!transfersData?.has_more}
                  onPrev={onPrevious}
                  onNext={handleNext}
                />
              </div>
            )}

            <div className="flex justify-end pt-6">{saveButton}</div>
          </Inner>
        </Loading>
      </Loading>
    </>
  );
};
